<template>
  <!-- Inline fallback for logos / multi-color assets -->
  <!-- eslint-disable vue/no-v-html, vue/html-self-closing -->
  <svg
    v-if="mode === 'inline' && inlineSvg?.contents"
    v-bind="{ ...inlineSvg.attributes, ...forwardedAttrs }"
    :width="props.width"
    :height="props.height"
    :aria-hidden="resolvedAriaHidden"
    :aria-label="resolvedAriaLabel"
    :role="resolvedAriaLabel ? 'img' : undefined"
    v-html="inlineSvg.contents"
  ></svg>
  <!-- eslint-enable vue/no-v-html, vue/html-self-closing -->
  <!-- Sprite reference: definition once in /sprites/ui-icons.svg -->
  <svg
    v-else-if="mode === 'sprite' && symbolId"
    v-bind="forwardedAttrs"
    class="ui-icon"
    :width="props.width"
    :height="props.height"
    :aria-hidden="resolvedAriaHidden"
    :aria-label="resolvedAriaLabel"
    :role="resolvedAriaLabel ? 'img' : undefined"
    focusable="false"
  >
    <use :href="`${spritePath}#${symbolId}`" width="100%" height="100%" />
  </svg>
  <svg
    v-else
    v-bind="forwardedAttrs"
    :width="props.width ?? 1"
    :height="props.height ?? 1"
    :aria-hidden="resolvedAriaHidden"
    :aria-label="resolvedAriaLabel"
    :role="resolvedAriaLabel ? 'img' : undefined"
  />
</template>

<script setup>
import {
  isInlineIconSrc,
  srcToSymbolId,
  SVG_SPRITE_PUBLIC_PATH
} from '~~/utils/svgSprite'

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  src: {
    type: String,
    default: null
  },
  width: {
    type: [String, Number],
    default: null
  },
  height: {
    type: [String, Number],
    default: null
  },
  /** Accessible name for meaningful icons. Decorative icons should omit this and use aria-hidden. */
  alt: {
    type: String,
    default: null
  },
  ariaHidden: {
    type: [Boolean, String],
    default: null
  }
})

const attrs = useAttrs()

const resolvedAriaLabel = computed(() => {
  if (props.alt) {
    return props.alt
  }
  const attrLabel = attrs['aria-label']
  return typeof attrLabel === 'string' ? attrLabel : undefined
})

const resolvedAriaHidden = computed(() => {
  if (props.ariaHidden !== null && props.ariaHidden !== undefined) {
    return props.ariaHidden === true || props.ariaHidden === 'true' ? 'true' : undefined
  }
  if (attrs['aria-hidden'] !== undefined) {
    return attrs['aria-hidden']
  }
  // Decorative by default when no accessible name is provided
  return resolvedAriaLabel.value ? undefined : 'true'
})

/** Fallthrough attrs without a11y keys we set explicitly. */
const forwardedAttrs = computed(() => {
  const {
    'aria-label': _ariaLabel,
    'aria-hidden': _ariaHidden,
    role: _role,
    ...rest
  } = attrs
  return rest
})

const spritePath = SVG_SPRITE_PUBLIC_PATH
const symbolId = computed(() => srcToSymbolId(props.src))
const mode = computed(() => {
  if (!props.src) {
    return 'empty'
  }
  if (isInlineIconSrc(props.src)) {
    return 'inline'
  }
  if (symbolId.value) {
    return 'sprite'
  }
  return 'empty'
})

function parseSvg (svg) {
  if (!svg) {
    return null
  }

  const regex = /<svg\s*(?<attr>.*?)>(?<contents>.*?)<\/svg>/s
  const matches = svg.match(regex)

  if (!matches || !matches.groups) {
    return null
  }

  const { contents, attr } = matches.groups

  const attributes = {}
  const attrMatches = attr.match(/([a-zA-Z-]+=".*?")/gs)

  if (attrMatches) {
    attrMatches.forEach((value) => {
      const attrMatch = value.match(/(?<name>[a-zA-Z-]+)="(?<value>.*)"/)
      if (attrMatch && attrMatch.groups) {
        const { name, value: attrValue } = attrMatch.groups
        attributes[name] = attrValue
      }
    })
  }

  if (props.width) {
    attributes.width = props.width
  }
  if (props.height) {
    attributes.height = props.height
  }

  // Strip conflicting a11y attrs from source SVG; we set them explicitly
  delete attributes['aria-hidden']
  delete attributes['aria-label']
  delete attributes.role
  delete attributes.alt

  return { contents, attributes }
}

const emptyInline = () => ({ contents: '', attributes: {} })

const { data: inlineSvg } = isInlineIconSrc(props.src) && props.src
  ? await useAsyncData(
    `icon-inline-${props.src}`,
    async () => {
      try {
        const response = await $fetch(props.src, {
          responseType: 'text'
        })
        return parseSvg(response) ?? emptyInline()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to load SVG: ${props.src}`, error)
        return emptyInline()
      }
    },
    {
      lazy: true,
      server: true,
      default: emptyInline,
      watch: [() => props.src]
    }
  )
  : { data: shallowRef(null) }
</script>

<style scoped>
.ui-icon {
  display: inline-block;
  overflow: hidden;
  vertical-align: middle;
}

.ui-icon use {
  fill: inherit;
  stroke: inherit;
}
</style>
